/**
 * Define as cores dos status juntamente com as traduções dos links.
 */
export const STATUS = {
    FILTERED: {
        translationKey: "link.status.filtered",
        bgColor: "#363636"
    },
    NON_LICENSED: {
        translationKey: "link.status.nonLicensed",
        bgColor: "#808080"
    },
    HW_FAIL: {
        translationKey: "link.status.hwFail",
        bgColor: "#b12525"
    },
    LOGICAL: {
        translationKey: "link.status.logical",
        bgColor: "#fa5904"
    },
    MASKED: {
        translationKey:  "link.status.masked",
        bgColor: "#FF00FF"
    },
    DISABLED: {
        translationKey: "link.status.disabled",
        bgColor: "#650000"
    },
    UNKNOWN: {
        translationKey: "link.status.unknow",
        bgColor: "#347ac1"
    },
    NORMAL: {
        translationKey: "link.status.normal",
         bgColor: "#1d9d23"
    },
    TEST: {
        translationKey: "link.status.test",
         bgColor: "#ffc800"
    },
    MINOR_FAULT: {
        translationKey: "link.status.minorFault",
        bgColor: "#dbd20d"
    },
    MAJOR_FAULT: {
        translationKey: "link.status.majorFault",
        bgColor: "#fa5904"
    },
    CRITICAL_FAULT: {
        translationKey: "link.status.criticalFault",
        bgColor: "#b12525"
    },
    STANDBY: {
        translationKey: "link.status.standby",
        bgColor: "#7fffd4"
    }
}

export const ICONS = {
    PORT_LINKED : "port_linked_link_icon",
    PCM_LINK : "pcm_link_icon",
    RDM_LINK : "rdm_link_icon",
    MPLS_LINK : "mpls_link_icon",
    BLM_LINK : "blm_link_icon",
    BLA_LINK : "bla_link_icon",
    PVLAN_INTERSWITCH_PORT : "pvlan_s_port_icon",
    PVLAN_COMMUNITY_PORT : "pvlan_c_port_icon",
    PVLAN_PROMISCUOUS_PORT : "pvlan_p_port_icon",
    PVLAN_ISOLATED_PORT : "pvlan_i_port_icon"
}

/**
 * Define a tradução de campos do tipo 'enum' para LinkSituation.
 */
export const LINK_SITUATION = {
    "ACTIVE" : {
        translationKey: "link.situation.active",
    },
    "NOT_ACTIVE" : {
        translationKey: "link.situation.notActive",
    }
}

/** Representa o modelo a ser utilizado na modal de links */
export interface LinkModal {
    linkInfo: Link;
}

/** Representa o modelo da modal de links */
export class Link {
    id?: number;
    name: string
    aEnd: EndLink;
    zEnd: EndLink;
    distance?: number;
    attenuation?: number;
    description: string;
    typeId: number;
    weight: number;
    generateName: boolean;

    constructor(name: string = "",
                aEnd: EndLink = new EndLink(),
                zEnd: EndLink = new EndLink(),
                distance: number = 0,
                attenuation: number = 0,
                description: string = "",
                typeId: number = 0,
                weight: number = 0,
                generatedName: boolean = false) {
        this.name = name;
        this.aEnd = aEnd;
        this.zEnd = zEnd;
        this.distance = distance;
        this.attenuation = attenuation;
        this.description = description;
        this.typeId = typeId;
        this.weight = weight;
        this.generateName = generatedName;
    }
}

/** Enum relacionado ao campo 'Situação' */
export enum LinkSituation {
    ACTIVE = 0,
    NOT_ACTIVE = 1
}

/** Eum relacionado ao campo 'Status' */
export enum Status {
    FILTERED = -7,
    NON_LICENSED = -6,
    HW_FAIL = -5,
    LOGICAL = -4,
    MASKED = -3,
    DISABLED = -2,
    UNKNOWN = -1,
    NORMAL = 0,
    TEST = 1,
    MINOR_FAULT = 2,
    MAJOR_FAULT = 3,
    CRITICAL_FAULT = 4,
    STANDBY = 5
}

/** Enum que representa os tipos de transmissão*/
export enum Transmission {
    TX   = "TX",
    RX   = "RX",
    NONE = "NONE"
}

/** Enum relacionado ao tipo de link da porta*/
export enum PortLinkType {
    STM1 = "STM-1",
    E3 = "E3",
    E1 = "E1",
    GSHDSL_TDM = "G.SHDSL TDM",
    V35 = "V35",
    V28 = "V28",
    G64 = "G64",
    MBB = "MBB",
    EM = "E&M",
    ETHERNET = "Ethernet",
    STM4 = "STM-4",
    STM16 = "STM-16",
    STM64 = "STM-64",
    AGG_ETH = "Aggregated Ethernet",
    PON = "PON",
    GSHDSL_EFM = "G.SHDSL EFM"
}

/* Representa o modelo de Slot */
export interface Slot {
    chassisNo: string
    slotNo: string;
    slotModelId: string;
    slotName: string;
    ports: Array<Port>;
}

/** Representa uma Transmissão */
export class EndTransmission<T extends Transmission> {
    transmission: T;
    linked: boolean;
    label?: string;
    constructor(transmission: T, linked: boolean, label?: string) {
        this.transmission = transmission;
        this.linked = linked;
        this.label = label;
    }
}

/** Representa uma Transmissão TX */
export class TxTransmission extends EndTransmission<Transmission.TX> {
    constructor(linked: boolean, label?: string) {
        super(Transmission.TX, linked, label);
    }
}

/** Representa uma Transmissão RX*/
export class RxTransmission extends EndTransmission<Transmission.RX> {
    constructor(linked: boolean, label?: string) {
        super(Transmission.RX, linked, label);
    }
}

/* Representa o modelo de MuxDemux para associação com Links */
export interface MuxDemux {
    resourceId: number
    name: string;
    txTransmission: TxTransmission;
    rxTransmission: RxTransmission;
}

/* Representa o modelo de Porta */
export interface Port {
    slotNo: number;
    portNo: number;
    portModelId: string;
    portName: string;
    txTransmission: TxTransmission;
    rxTransmission: RxTransmission;
    portChannel: boolean;
    linked: boolean;
    memberOfPortChannel: boolean;
    hasRdmEnabled: boolean;
    backupLinkMain: boolean;
    backupLinkAlternative: boolean;
    mplsUplink: boolean;
    privateVlanPortInterSwitch: boolean;
    privateVlanPortPromiscuous: boolean;
    privateVlanPortCommunity: boolean;
    privateVlanPortIsolated: boolean;
}

/* Representa o modelo a ser utilizado nos tipos de links */
export interface LinkType {
    id: number;
    label: string;
    defaultWeight: number;
}

/* Representa o modelo da listagem de links */
export interface LinkInfo {
    id: number;
    name: string;
    aEndResourceId: number;
    aEnd: string;
    aEndTransmission: string;
    aEndPortModel: string;
    zEndResourceId: number;
    zEnd: string;
    zEndTransmission: string;
    zEndPortModel: string;
    distance?: number;
    attenuation?: number;
    typeDescription: string;
    linkSituation: LinkSituation;
    linkStatus: Status;
    typeId: number;
    weight: number;
    description?: string;
}

/** Representa os dados de cada link utilizados para geração do nome */
export class EndLink {
    deviceResourceId: number;
    chassisNo: string;
    slotNo: string;
    portNo: number;
    portModel: string;
    transmission: Transmission;
    muxDemuxResourceId: number;
    muxDemux: boolean;

    constructor(deviceResourceId: number = 0, chassisNo: string = "-1", slotNo: string = "-1", portNo: number = -1,
            portModel: string = null, transmission: Transmission = Transmission.NONE, muxDemuxResourceId: number = null,
            muxDemux: boolean = false) {
        this.deviceResourceId = deviceResourceId;
        this.chassisNo = chassisNo;
        this.slotNo = slotNo;
        this.portNo = portNo;
        this.portModel = portModel;
        this.transmission = transmission;
        this.muxDemuxResourceId = muxDemuxResourceId;
        this.muxDemux = muxDemux;
    }
}
